"use client";

import type { PortfolioMetrics } from "@/types/portfolio-builder.types";

interface Props {
  metrics: PortfolioMetrics;
}

function fmt(n: number, decimals = 2): string {
  return n.toFixed(decimals);
}

function fmtPct(n: number): string {
  return (n * 100).toFixed(2) + "%";
}

function fmtCurrency(n: number): string {
  return "$" + Math.round(n).toLocaleString("en-US");
}

export function PortfolioMetricsPanel({ metrics }: Props) {
  const items = [
    {
      label: "Total Equity",
      value: fmtCurrency(metrics.totalEquity),
      color: "text-blue-400",
    },
    {
      label: "Avg Year Return",
      value: fmtPct(metrics.averageYear),
      color: metrics.averageYear >= 0 ? "text-green-400" : "text-red-400",
    },
    { label: "Max Drawdown", value: fmtPct(metrics.maxDrawdown), color: "text-red-400" },
    { label: "Win Rate", value: fmt(metrics.winRate, 1) + "%", color: "text-green-400" },
    { label: "Sharpe Ratio", value: fmt(metrics.sharpeRatio, 3), color: "text-blue-400" },
    {
      label: "Sortino Ratio",
      value: fmt(metrics.sortinoRatio, 3),
      color: "text-blue-400",
    },
    { label: "Calmar Ratio", value: fmt(metrics.calmarRatio, 3), color: "text-blue-400" },
    {
      label: "Total Trades",
      value: metrics.totalTrades.toLocaleString(),
      color: "text-gray-300",
    },
    {
      label: "Winning",
      value: metrics.winningTrades.toLocaleString(),
      color: "text-green-400",
    },
    {
      label: "Losing",
      value: metrics.losingTrades.toLocaleString(),
      color: "text-red-400",
    },
    {
      label: "Avg Winner",
      value: fmtCurrency(metrics.averageWinner),
      color: "text-green-400",
    },
    {
      label: "Avg Loser",
      value: fmtCurrency(metrics.averageLoser),
      color: "text-red-400",
    },
  ];

  return (
    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
      {items.map(({ label, value, color }) => (
        <div
          key={label}
          className="rounded-lg border border-gray-800 bg-gray-900/50 p-3 text-center"
        >
          <div className="text-xs text-gray-500">{label}</div>
          <div className={`mt-1 text-sm font-semibold ${color}`}>{value}</div>
        </div>
      ))}
    </div>
  );
}
